import React from 'react';
import { styled } from '@mui/material/styles';
import { TempTaskFuncDto } from 'models/dtoTypes';
import { withRouter } from 'react-router';
import { getTempTasksApi } from 'services';
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const PREFIX = 'TempTasksPage';

const classes = {
  root: `${PREFIX}-root`,
  demo: `${PREFIX}-demo`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`&.${classes.demo}`]: {
    backgroundColor: theme.palette.background.paper,
  },
}));

export function TempTasksPage(): JSX.Element {
  const [tasks, setTasks] = React.useState<TempTaskFuncDto[]>([]);
  const [selectedTask, setSelectedTask] = React.useState<TempTaskFuncDto>();
  const funcName = selectedTask?.funcName;
  const funcDesc = selectedTask?.funcDesc;
  const [funcData, setFuncData] = React.useState<string>('');
  const [info, setInfo] = React.useState('');

  const [submitting, setSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const tempTaskApi = await getTempTasksApi();
        const tempTasks = (await tempTaskApi.getTempTasks()).data;
        setTasks(tempTasks || []);
      } catch (e) {
        console.error(e);
      }
    };
    loadData();
  }, []);

  const renderResult = () => {
    if (!info) {
      return '';
    }
    if (typeof info === 'string') {
      return (
        <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(JSON.parse(info), null, 2)}</pre>
      );
    }
    return (
      <div style={{ marginTop: 10 }}>
        <div>Result:</div>
        <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(info, null, 2)}</pre>
      </div>
    );
  };

  async function handleExecute(e: React.FormEvent) {
    e.stopPropagation();
    if (!funcName) {
      return '';
    }
    setSubmitting(true);
    try {
      const tempTaskApi = await getTempTasksApi();
      const result = (await tempTaskApi.executeTempTask({ funcName, funcData })).data;
      setInfo(result);
    } catch (e) {
      console.error(e);
      setInfo('Cannot execute temp task');
    }
    setSubmitting(false);
  }

  const handleSelectFunction = (e: SelectChangeEvent<string | null>) => {
    const funcName = e.target.value;
    const task = tasks.find((t) => t.funcName === funcName);
    setSelectedTask(task);
  };

  // nested form is bad, change to element 'div'
  const renderForm = () => {
    return (
      <div>
        <Stack direction="column" spacing={2} marginTop={3}>
          <FormControl fullWidth>
            <InputLabel id="temp-task-name">Task Name</InputLabel>
            <Select
              labelId="temp-task-name"
              id="temp-task-name-select"
              value={funcName}
              label="Task Name"
              onChange={handleSelectFunction}
            >
              {tasks.map((task) => (
                <MenuItem key={task.funcName || ''} value={task.funcName || ''}>
                  {task.funcName}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{funcDesc}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              label="Task Data(JSON or string)"
              multiline
              variant="outlined"
              onChange={(e) => setFuncData(e.target.value)}
              value={funcData}
            />
          </FormControl>
          <Button
            variant="contained"
            disabled={!funcName || submitting}
            color="primary"
            autoFocus
            onClick={handleExecute}
          >
            Execute
          </Button>
        </Stack>
      </div>
    );
  };

  return (
    <Root>
      <Stack direction="column" spacing={1}>
        <h2>Temp Tasks</h2>

        <Typography variant="h6" sx={{ fontWeight: 'regular' }}>
          Caution: execute one-time tasks
        </Typography>

        {renderForm()}
        {renderResult()}
      </Stack>
    </Root>
  );
}

export default withRouter(TempTasksPage);
